// @flow
import { Stack } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  AddIcon,
  TrashIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import { Banner } from '@fortress-technology-solutions/fortress-component-library/Molecules_Portal';
import Button from 'components/Atoms/Button';
import moment from 'moment/moment';
import { FormattedMessage } from 'react-intl';
import { validateNoSpecialChars } from 'utils/validateNoSpecialChars';
import { BUTTON_GROUP_SX } from './constants';
import {
  BONUS_COMMISSION_OPTIONS,
  EMPLOYMENT_TYPE_OPTIONS,
  FIELD_AMOUNT,
  FIELD_COUNTER_REQUIRED,
  FIELD_DATE,
  FIELD_GROUP_BUTTONS,
  FIELD_SEASONAL_WORK_DATES,
  FIELD_SHOW_ANTICIPATED12,
  FIELD_SHOW_ANTICIPATED12_ANNUAL,
  FIELD_SHOW_ANTICIPATED12_BIWEEKLY,
  FIELD_SHOW_ANTICIPATED12_HOURLY,
  FIELD_SHOW_ANTICIPATED12_MONTHLY,
  FIELD_SHOW_ANTICIPATED12_SEMI_MONTHLY,
  FIELD_SHOW_ANTICIPATED12_WEEKLY,
  FIELD_SHOW_COMMISSIONS_PAY,
  FIELD_SHOW_COMMISSIONS_PAY_ANNUALLY,
  FIELD_SHOW_COMMISSIONS_PAY_MONTHLY,
  FIELD_SHOW_COMMISSIONS_PAY_WEEKLY,
  FIELD_SHOW_COMMISSIONS_SPECIFIED,
  FIELD_SHOW_COMMON,
  FIELD_SHOW_MILITARY,
  FIELD_SHOW_NO_MILITARY,
  FIELD_SHOW_NO_MILITARY_NO_SELFEMPLOYED_PAYRATE,
  FIELD_SHOW_NO_MILITARY_OR_SELF_EMPLOYED,
  FIELD_SHOW_NO_SELF_EMPLOYED,
  FIELD_SHOW_OVERTIME_PAY,
  FIELD_SHOW_PAY_ANNUALLY,
  FIELD_SHOW_PAY_HOURLY_SEMI_OR_BIWEEKLY,
  FIELD_SHOW_PAY_MONTHLY,
  FIELD_SHOW_PAY_WEEKLY,
  FIELD_SHOW_SEASONAL,
  FIELD_SHOW_SELF_EMPLOYED,
  FIELD_SHOW_TIPS_PAY,
  FIELD_SHOW_TIPS_UNREPORTED,
  FIELD_TEXT_REQUIRED,
  FIELD_YES_NO,
  GridProps,
  JOB_FIELD_NAMES,
  LAYOFF_PERIOD_OPTIONS,
  MILITARY_BRANCHES_OPTIONS,
  PAY_FREQUENCY_COMMISSION_OPTIONS,
  SMALL_GRID_PROPS,
  MESSAGE_IDS as BASE_MESSAGE_IDS,
  FIELD_SHOW_IF_EMPLOYMENT_TYPE_IS_SELECTED,
  EMPLOYMENT_TYPE,
  ADDRESS_FIELDS,
  FIELD_YES_UPDATE,
  FIELD_SHOW_ANTICIPATED12_HOURLY_UPDATE,
} from './constants.job';
import { FIELD_MEMBERS } from 'pages/AffordaWagesAndIncome/constants.fields';
import { PAY_FREQUENCY } from './constants';

export const MESSAGE_IDS = {
  ...BASE_MESSAGE_IDS,
  title: 'afforda.wagesAndIncome.details.job.title',
};

export const PAY_FREQUENCY_OPTIONS = {
  HOURLY: {
    value: PAY_FREQUENCY.hourly,
    text: 'afforda.wagesAndIncome.details.job.hourly',
  },
  ANNUALLY: {
    value: PAY_FREQUENCY.annually,
    text: 'afforda.wagesAndIncome.details.job.annually',
  },
};

const POSITION_FIELD = ({
  intl,
  isMilitaryEmploymentTypeSelected,
}): Object => ({
  ...FIELD_TEXT_REQUIRED,
  ...FIELD_SHOW_IF_EMPLOYMENT_TYPE_IS_SELECTED,
  label: (
    <FormattedMessage
      id={`afforda.wagesAndIncome.details.job.${
        isMilitaryEmploymentTypeSelected ? 'military.position' : 'position'
      }`}
    />
  ),
  name: JOB_FIELD_NAMES.position,
  rules: {
    required: true,
    validate: validateNoSpecialChars,
  },
  GridProps,
  placeholder: intl?.formatMessage?.({
    id: `afforda.wagesAndIncome.details.job.${
      isMilitaryEmploymentTypeSelected ? 'military.position' : 'position'
    }`,
  }),
});

const getStartDateLabelId = (employmentType) => {
  switch (employmentType) {
    case EMPLOYMENT_TYPE.selfEmployed:
      return 'afforda.wagesAndIncome.details.job.startDateBusiness';
    case EMPLOYMENT_TYPE.military:
      return 'afforda.wagesAndIncome.details.job.military.serviceStartDate';
    default:
      return 'afforda.wagesAndIncome.details.job.startDate';
  }
};

const START_DATE_FIELD = ({
  employmentType,
  isMilitaryEmploymentTypeSelected,
}) => ({
  ...FIELD_DATE,
  ...FIELD_SHOW_IF_EMPLOYMENT_TYPE_IS_SELECTED,
  label: <FormattedMessage id={getStartDateLabelId(employmentType)} />,
  name: JOB_FIELD_NAMES.startDate,
  disableFuture: true,
  defaultCalendarMonth: moment(2000, 'YYYY'),
  rules: {
    ...(!isMilitaryEmploymentTypeSelected && {
      minDate: moment().subtract(50, 'year'),
    }),
    maxDate: moment().startOf('day'),
    required: true,
    validate: (value) => {
      if (!value || !moment(value).isValid()) {
        return 'Invalid date format';
      }

      const now = moment().startOf('day');
      const date = moment(value);

      if (isMilitaryEmploymentTypeSelected) {
        if (date.isAfter(now)) {
          return 'Date cannot be in the future';
        }
      } else {
        const minDate = moment().subtract(50, 'year');
        if (date.isBefore(minDate)) {
          return 'Date cannot be more than 50 years ago';
        }
        if (date.isAfter(now)) {
          return 'Date cannot be in the future';
        }
      }
      return true;
    },
  },
  GridProps: SMALL_GRID_PROPS,
});

export const FIELDS = ({
  anticipatedIncreaseWhen,
  disabledMembers,
  employmentType,
  hourlyPay,
  intl,
  isMilitaryEmploymentDisabled,
  isMoreDates,
  members,
  states,
  onChangeMoreDates,
  validateZip,
  hasAdoptedHOTMA,
}) => {
  const isMilitaryEmploymentTypeSelected =
    employmentType === EMPLOYMENT_TYPE.military;
  const isSelfEmploymentTypeSelected =
    employmentType === EMPLOYMENT_TYPE.selfEmployed;

  const hourlyPayRateUpdated =
    hourlyPay?.length && anticipatedIncreaseWhen?.length
      ? `$ ${(
          parseFloat(hourlyPay) + parseFloat(anticipatedIncreaseWhen)
        ).toFixed(2)}`
      : '';

  return [
    ...FIELD_MEMBERS({ members, disabledMembers }),
    {
      ...FIELD_GROUP_BUTTONS,
      ...FIELD_SHOW_COMMON,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.employmentType" />
      ),
      name: JOB_FIELD_NAMES.employmentType,
      options: Object.values(EMPLOYMENT_TYPE_OPTIONS)?.map((option) => ({
        value: option.value,
        label: <FormattedMessage id={option.text} />,
        disabled:
          option.value === EMPLOYMENT_TYPE.military &&
          isMilitaryEmploymentDisabled,
      })),
    },
    ...(isMilitaryEmploymentTypeSelected
      ? [POSITION_FIELD({ intl, isMilitaryEmploymentTypeSelected })]
      : []),

    ...(isMilitaryEmploymentTypeSelected
      ? [
          START_DATE_FIELD({
            intl,
            employmentType,
            isMilitaryEmploymentTypeSelected,
            isSelfEmploymentTypeSelected,
          }),
        ]
      : []),
    // Start Date - No Military, No Self Employed
    ...(!isMilitaryEmploymentTypeSelected
      ? [
          POSITION_FIELD({
            intl,
            isMilitaryEmploymentTypeSelected,
          }),
        ]
      : []),

    ...(!isMilitaryEmploymentTypeSelected && !isSelfEmploymentTypeSelected
      ? [
          START_DATE_FIELD({
            intl,
            employmentType,
            isMilitaryEmploymentTypeSelected,
            isSelfEmploymentTypeSelected,
          }),
        ]
      : []),
    // ----------------
    // Option - Military
    // ----------------
    {
      ...FIELD_GROUP_BUTTONS,
      ...FIELD_SHOW_MILITARY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.military.branch" />
      ),
      name: JOB_FIELD_NAMES.militaryBranch,
      rules: {
        required: true,
      },
      options: Object.values(MILITARY_BRANCHES_OPTIONS)?.map((option) => ({
        value: option.value,
        label: <FormattedMessage id={option.text} />,
      })),
    },
    {
      ...FIELD_COUNTER_REQUIRED,
      ...FIELD_SHOW_MILITARY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.military.numDependents" />
      ),
      name: JOB_FIELD_NAMES.numDependents,
    },

    // Common - Company
    {
      ...FIELD_TEXT_REQUIRED,
      ...FIELD_SHOW_NO_MILITARY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.company.name" />
      ),
      placeholder: intl?.formatMessage?.({
        id: 'afforda.wagesAndIncome.details.job.company.name',
      }),
      name: JOB_FIELD_NAMES.employerName,
    },
    //
    // Supervisor - No Self Employed
    //
    {
      ...FIELD_TEXT_REQUIRED,
      ...FIELD_SHOW_NO_SELF_EMPLOYED,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.supervisor.name" />
      ),
      name: JOB_FIELD_NAMES.supervisorName,
      placeholder: intl?.formatMessage?.({
        id: 'afforda.wagesAndIncome.details.job.supervisor.name',
      }),
    },
    {
      ...FIELD_TEXT_REQUIRED,
      ...FIELD_SHOW_NO_SELF_EMPLOYED,
      variant: 'phone',
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.supervisor.phone" />
      ),
      name: JOB_FIELD_NAMES.supervisorPhone,
      placeholder: intl?.formatMessage?.({
        id: 'afforda.wagesAndIncome.details.job.supervisor.phone',
      }),
    },
    // Work Type - Self Employed Only
    {
      ...FIELD_TEXT_REQUIRED,
      ...FIELD_SHOW_SELF_EMPLOYED,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.workType" />
      ),
      name: JOB_FIELD_NAMES.workType,
      placeholder: intl?.formatMessage?.({
        id: 'afforda.wagesAndIncome.details.job.workType',
      }),
    },
    //
    // Employment Type - Self Employed
    //
    ...(isSelfEmploymentTypeSelected
      ? [
          START_DATE_FIELD({
            intl,
            employmentType,
            isMilitaryEmploymentTypeSelected,
            isSelfEmploymentTypeSelected,
          }),
          {
            variant: 'custom',
            readOnly: true,
            component: (
              <Banner
                sx={{ width: '100%' }}
                message={
                  <FormattedMessage id="afforda.wagesAndIncome.details.job.noPreviousTaxReturns" />
                }
                status={'info'}
                variant={'simple'}
              />
            ),
          },
        ]
      : []),
    {
      ...FIELD_COUNTER_REQUIRED,
      ...FIELD_SHOW_SELF_EMPLOYED,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.selfTaxes" />
      ),
      name: JOB_FIELD_NAMES.selfTaxes,
    },
    {
      ...FIELD_AMOUNT,
      ...FIELD_SHOW_SELF_EMPLOYED,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.averageNet" />
      ),
      name: JOB_FIELD_NAMES.averageNet,
    },
    {
      ...FIELD_AMOUNT,
      ...FIELD_SHOW_SELF_EMPLOYED,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.annualNet" />
      ),
      name: JOB_FIELD_NAMES.annualNet,
    },
    // Employment Type - Seasonal
    {
      ...FIELD_COUNTER_REQUIRED,
      ...FIELD_SHOW_SEASONAL,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.workWeeks" />
      ),
      name: JOB_FIELD_NAMES.seasonalWorkWeeks,
      rules: {
        required: true,
        min: 1,
        max: 51,
      },
    },
    {
      ...FIELD_SEASONAL_WORK_DATES,
      variant: 'date',
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.doNotwork" />
      ),
      name: JOB_FIELD_NAMES.doNotWorkStartDate,
      placeholder: intl?.formatMessage?.({
        id: 'afforda.wagesAndIncome.details.job.start',
      }),
      disableFuture: true,
      defaultCalendarMonth: moment(2000, 'YYYY'),
      rules: {
        maxDate: moment().startOf('day'),
        required: true,
      },
      GridProps: SMALL_GRID_PROPS,
    },
    {
      ...FIELD_SHOW_SEASONAL,
      variant: 'date',
      name: JOB_FIELD_NAMES.doNotWorkEndDate,
      label: 'Do Not Work End Date',
      placeholder: intl?.formatMessage?.({
        id: 'afforda.wagesAndIncome.details.job.end',
      }),
      disableFuture: true,
      defaultCalendarMonth: moment(2000, 'YYYY'),
      rules: {
        minDate: moment().subtract(50, 'year'),
        maxDate: moment().startOf('day'),
        required: true,
      },
      GridProps: SMALL_GRID_PROPS,
      InputLabelProps: {
        sx: {
          visibility: 'hidden',
        },
      },
      helperText: !isMoreDates && (
        <Stack
          alignItems={'flex-end'}
          sx={{ marginTop: 1, marginRight: '-14px' }}
        >
          <Button
            onClick={onChangeMoreDates}
            variant="secondary"
            startIcon={<AddIcon />}
          >
            <FormattedMessage id="afforda.wagesAndIncome.details.job.addDates" />
          </Button>
        </Stack>
      ),
    },

    // SEASONAL - More Dates
    {
      ...FIELD_SEASONAL_WORK_DATES,
      variant: 'date',
      name: JOB_FIELD_NAMES.doNotWorkStartDate2,
      placeholder: intl?.formatMessage?.({
        id: 'afforda.wagesAndIncome.details.job.start',
      }),
      disableFuture: true,
      defaultCalendarMonth: moment(2000, 'YYYY'),
      rules: {
        maxDate: moment().startOf('day'),
        required: isMoreDates,
      },
      GridProps: SMALL_GRID_PROPS,
      hidden: !isMoreDates,
    },
    {
      ...FIELD_SHOW_SEASONAL,
      variant: 'date',
      name: JOB_FIELD_NAMES.doNotWorkEndDate2,
      label: 'End Date',
      placeholder: intl?.formatMessage?.({
        id: 'afforda.wagesAndIncome.details.job.end',
      }),
      disableFuture: true,
      defaultCalendarMonth: moment(2000, 'YYYY'),
      rules: {
        minDate: moment().subtract(50, 'year'),
        maxDate: moment().startOf('day'),
        required: isMoreDates,
      },
      GridProps: SMALL_GRID_PROPS,
      InputLabelProps: {
        sx: {
          visibility: 'hidden',
        },
      },
      hidden: !isMoreDates,
      helperText: isMoreDates && (
        <Stack
          alignItems={'flex-end'}
          sx={{ marginTop: 1, marginRight: '-14px' }}
        >
          <Button
            sx={{ marginLeft: 'auto' }}
            onClick={onChangeMoreDates}
            variant="secondary"
            startIcon={<TrashIcon />}
          >
            <FormattedMessage id="afforda.wagesAndIncome.details.job.removeDates" />
          </Button>
        </Stack>
      ),
    },

    // Address fields
    ...ADDRESS_FIELDS({ intl, states, validateZip }),

    // Military - After Supervisor
    {
      variant: 'custom',
      readOnly: true,
      component: (
        <Banner
          sx={{
            width: '100%',
            display:
              employmentType === EMPLOYMENT_TYPE.military ? undefined : 'none',
          }}
          message={
            <FormattedMessage id="afforda.wagesAndIncome.details.job.military.disclaimer" />
          }
          status={'info'}
          variant={'simple'}
        />
      ),
    },
    {
      variant: 'formSubheading',
      readOnly: true,
      sx: {
        display:
          employmentType === EMPLOYMENT_TYPE.military ? undefined : 'none',
      },
      children: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.military.monthlyIncome" />
      ),
    },
    {
      ...FIELD_AMOUNT,
      ...FIELD_SHOW_MILITARY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.military.basePay" />
      ),
      name: JOB_FIELD_NAMES.basePayLongevity,
      GridProps: {
        xs: 12,
        sm: 12,
      },
    },
    {
      ...FIELD_AMOUNT,
      ...FIELD_SHOW_MILITARY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.military.proficiency" />
      ),
      name: JOB_FIELD_NAMES.proficiency,
      GridProps: {
        xs: 12,
        sm: 12,
      },
    },
    {
      ...FIELD_AMOUNT,
      ...FIELD_SHOW_MILITARY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.military.foreignDuty" />
      ),
      name: JOB_FIELD_NAMES.foreignDuty,
    },
    {
      ...FIELD_AMOUNT,
      ...FIELD_SHOW_MILITARY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.military.subsistence" />
      ),
      name: JOB_FIELD_NAMES.subsistence,
    },
    {
      ...FIELD_AMOUNT,
      ...FIELD_SHOW_MILITARY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.military.quarters" />
      ),
      name: JOB_FIELD_NAMES.quarters,
    },

    // Common - Pay Frequency (No Military)
    {
      ...FIELD_SHOW_NO_SELF_EMPLOYED,
      variant: 'custom',
      readOnly: true,
      component: (
        <Banner
          sx={{
            width: '100%',
            display:
              !employmentType?.length ||
              employmentType === EMPLOYMENT_TYPE.military
                ? 'none'
                : undefined,
          }}
          message={
            <FormattedMessage
              id="afforda.wagesAndIncome.details.job.disclaimer"
              values={{ count: hasAdoptedHOTMA ? '2' : '4-6' }}
            />
          }
          status={'info'}
          variant={'simple'}
        />
      ),
    },

    {
      ...FIELD_GROUP_BUTTONS,
      ...FIELD_SHOW_NO_MILITARY_OR_SELF_EMPLOYED,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.payFrequency" />
      ),
      name: JOB_FIELD_NAMES.payFrequency,
      rules: {
        required: true,
      },
      options: Object.values(PAY_FREQUENCY_OPTIONS)?.map((option) => ({
        value: option.value,
        label: <FormattedMessage id={option.text} />,
      })),
    },

    //
    // Pay Frequency - Hourly
    {
      ...FIELD_AMOUNT,
      ...FIELD_SHOW_PAY_HOURLY_SEMI_OR_BIWEEKLY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.payFrequency.hourly" />
      ),
      name: JOB_FIELD_NAMES.hourlyPay,
    },
    {
      ...FIELD_SHOW_PAY_HOURLY_SEMI_OR_BIWEEKLY,
      variant: 'number',
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.payFrequency.avgHours" />
      ),
      name: JOB_FIELD_NAMES.hoursPerWeek,
      rules: {
        required: true,
      },
      GridProps,
    },

    // ----------------
    // Pay Frequency - Weekly
    // ----------------
    {
      ...FIELD_AMOUNT,
      ...FIELD_SHOW_PAY_WEEKLY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.payFrequency.weekly" />
      ),
      name: JOB_FIELD_NAMES.weeklyPay,
    },

    // ----------------
    // Pay Frequency - Monthly
    // ----------------
    {
      ...FIELD_AMOUNT,
      ...FIELD_SHOW_PAY_MONTHLY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.payFrequency.monthly" />
      ),
      name: JOB_FIELD_NAMES.monthlyPay,
    },

    // ----------------
    // Pay Frequency - Annually
    // ----------------
    {
      ...FIELD_AMOUNT,
      ...FIELD_SHOW_PAY_ANNUALLY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.payFrequency.annual" />
      ),
      name: JOB_FIELD_NAMES.annualPay,
    },

    // ----------------
    // Anticipated 12 months - No Military, No Self Employed
    // ----------------
    {
      ...FIELD_YES_NO,
      ...FIELD_SHOW_NO_MILITARY_NO_SELFEMPLOYED_PAYRATE,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.increasePay" />
      ),
      name: JOB_FIELD_NAMES.anticipatedIncrease12Months,
    },
    {
      ...FIELD_AMOUNT,
      ...FIELD_SHOW_ANTICIPATED12_ANNUAL,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.increasePay.annual" />
      ),
      name: JOB_FIELD_NAMES.anticipatedIncreaseRate,
      helperText: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.increasePay.provideOnly" />
      ),
      gridProps: {
        xs: 12,
        sm: 12,
      },
    },
    {
      ...FIELD_AMOUNT,
      ...FIELD_SHOW_ANTICIPATED12_HOURLY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.increasePay.hourly" />
      ),
      name: JOB_FIELD_NAMES.anticipatedIncreaseRate,
      helperText: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.increasePay.provideOnly" />
      ),
      gridProps: {
        xs: 12,
        sm: 12,
      },
    },
    {
      ...FIELD_AMOUNT,
      ...FIELD_SHOW_ANTICIPATED12_WEEKLY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.increasePay.weekly" />
      ),
      name: JOB_FIELD_NAMES.anticipatedIncreaseRate,
      helperText: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.increasePay.provideOnly" />
      ),
      gridProps: {
        xs: 12,
        sm: 12,
      },
    },
    {
      ...FIELD_AMOUNT,
      ...FIELD_SHOW_ANTICIPATED12_BIWEEKLY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.increasePay.biWeekly" />
      ),
      name: JOB_FIELD_NAMES.anticipatedIncreaseRate,
      helperText: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.increasePay.provideOnly" />
      ),
      gridProps: {
        xs: 12,
        sm: 12,
      },
    },
    {
      ...FIELD_AMOUNT,
      ...FIELD_SHOW_ANTICIPATED12_MONTHLY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.increasePay.monthly" />
      ),
      name: JOB_FIELD_NAMES.anticipatedIncreaseRate,
      helperText: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.increasePay.provideOnly" />
      ),
      gridProps: {
        xs: 12,
      },
    },
    {
      ...FIELD_AMOUNT,
      ...FIELD_SHOW_ANTICIPATED12_SEMI_MONTHLY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.increasePay.semiMonthly" />
      ),
      name: JOB_FIELD_NAMES.anticipatedIncreaseRate,
      helperText: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.increasePay.provideOnly" />
      ),
    },
    {
      ...FIELD_YES_UPDATE,
      ...FIELD_SHOW_ANTICIPATED12_HOURLY_UPDATE,
      label: (
        <FormattedMessage
          id="afforda.wagesAndIncome.details.job.increasePay.updateAsk"
          values={{
            amount: hourlyPayRateUpdated,
          }}
        />
      ),
      name: 'anticipatedIncreaseUpdate',
    },
    {
      ...FIELD_DATE,
      ...FIELD_SHOW_ANTICIPATED12,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.increasePay.when" />
      ),
      defaultCalendarMonth: moment(moment().month(), 'mm'),
      name: JOB_FIELD_NAMES.anticipatedIncreaseWhen,
      disablePast: true,
      rules: {
        minDate: moment().startOf('day'),
        maxDate: moment().add(1, 'year'),
        required: true,
      },
      GridProps: {
        xs: 12,
        sm: 12,
      },
    },

    // ----------------
    // Overtime Pay
    // ----------------
    {
      ...FIELD_YES_NO,
      ...FIELD_SHOW_NO_MILITARY_NO_SELFEMPLOYED_PAYRATE,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.overtime" />
      ),
      name: JOB_FIELD_NAMES.overtimePay,
    },
    {
      ...FIELD_AMOUNT,
      ...FIELD_SHOW_OVERTIME_PAY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.overtime.payRate" />
      ),
      name: JOB_FIELD_NAMES.overtimeHourRate,
    },
    {
      ...FIELD_COUNTER_REQUIRED,
      ...FIELD_SHOW_OVERTIME_PAY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.overtime.avgHours" />
      ),
      name: JOB_FIELD_NAMES.overtimeAvgHours,
    },

    // ----------------
    // Tips and Gratuities
    // ----------------
    {
      ...FIELD_YES_NO,
      ...FIELD_SHOW_NO_MILITARY_NO_SELFEMPLOYED_PAYRATE,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.receiveTips" />
      ),
      name: JOB_FIELD_NAMES.tipsAndGratuities,
    },
    {
      ...FIELD_YES_NO,
      ...FIELD_SHOW_TIPS_PAY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.receiveTips.all" />
      ),
      name: JOB_FIELD_NAMES.tipsAndGratuitiesAllReported,
    },
    {
      ...FIELD_AMOUNT,
      ...FIELD_SHOW_TIPS_PAY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.receiveTips.avgWeek" />
      ),
      name: JOB_FIELD_NAMES.receiveTipsAvgWeek,
    },
    {
      ...FIELD_AMOUNT,
      ...FIELD_SHOW_TIPS_UNREPORTED,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.receiveTips.avgUnreported" />
      ),
      name: JOB_FIELD_NAMES.receiveTipsAvgUnreported,
    },

    // ----------------
    // Commissions or Bonuses
    // ----------------
    {
      ...FIELD_YES_NO,
      ...FIELD_SHOW_NO_MILITARY_NO_SELFEMPLOYED_PAYRATE,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.commissions" />
      ),
      name: JOB_FIELD_NAMES.commissionsOrBonuses,
    },
    {
      ...FIELD_GROUP_BUTTONS,
      ...FIELD_SHOW_COMMISSIONS_PAY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.commissions.specify" />
      ),
      name: JOB_FIELD_NAMES.bonusOrCommissions,
      options: Object.values(BONUS_COMMISSION_OPTIONS)?.map((option) => ({
        value: option.value,
        label: <FormattedMessage id={option.text} />,
      })),
      helperText: (
        <FormattedMessage id="afforda.wagesAndIncome.details.selectAll" />
      ),
      sx: {
        ...BUTTON_GROUP_SX,
      },
    },
    {
      ...FIELD_GROUP_BUTTONS,
      ...FIELD_SHOW_COMMISSIONS_SPECIFIED,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.commissions.payFrequency" />
      ),
      name: JOB_FIELD_NAMES.commissionsPayFrequency,
      options: Object.values(PAY_FREQUENCY_COMMISSION_OPTIONS)?.map(
        (option) => ({
          value: option.value,
          label: <FormattedMessage id={option.text} />,
        }),
      ),
      direction: 'row',
      sx: {
        ...BUTTON_GROUP_SX,
      },
    },
    {
      ...FIELD_AMOUNT,
      ...FIELD_SHOW_COMMISSIONS_PAY_WEEKLY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.commissions.avgWeekly" />
      ),
      name: JOB_FIELD_NAMES.commissionsPayFrequencyWeekly,
    },
    {
      ...FIELD_AMOUNT,
      ...FIELD_SHOW_COMMISSIONS_PAY_MONTHLY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.commissions.avgMonthly" />
      ),
      name: JOB_FIELD_NAMES.commissionsPayFrequencyMonthly,
    },
    {
      ...FIELD_AMOUNT,
      ...FIELD_SHOW_COMMISSIONS_PAY_ANNUALLY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.commissions.avgAnnually" />
      ),
      name: JOB_FIELD_NAMES.commissionsPayFrequencyAnnually,
    },

    // ----------------
    // Layoff Period
    // ----------------
    {
      ...FIELD_SHOW_SEASONAL,
      variant: 'buttonGroup',
      multiselect: true,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.layoffPeriod" />
      ),
      name: JOB_FIELD_NAMES.layoffPeriod,
      GridProps: {
        xs: 12,
      },
      rules: {
        required: true,
      },
      helperText: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.layoffPeriod.selectAll" />
      ),
      direction: 'column',
      options: Object.values(LAYOFF_PERIOD_OPTIONS)?.map((option) => ({
        value: option.value,
        label: <FormattedMessage id={option.text} />,
      })),
    },
  ];
};

export const CURRENT_JOB_FIELD_NAMES = FIELDS({}).reduce((acc, field) => {
  acc[field.name] = field.name;
  return acc;
}, {});
